<template>
  <section :id="id" aria-roledescription="carousel" class="onboarding-carousel" role="region">
    <header>
      <h2 class="headline-h4-headline">
        {{ $t('highfrequencygame.hub.onboarding.headline') }}
      </h2>
    </header>

    <LcCarousel
      :id="`${id}-carousel`"
      class="carousel"
      :disable-swipe="isDesktop"
      :key-extractor="(_, index) => String(index)"
      :slides="steps"
    >
      <template #slide="{ attributes, slide }">
        <LcCard
          v-bind="attributes"
          class="card mr-100 bg-secondary-secondary"
          radius="large"
          shadow="large"
        >
          <template #header>
            <img alt="" aria-hidden="true" class="image" :src="slide.image" />
          </template>

          <h3 class="headline-h5-headline text-primary-primary">
            {{ slide.headline }}
          </h3>

          <p class="mt-50 text-secondary-secondary">{{ slide.description }}</p>
        </LcCard>
      </template>
    </LcCarousel>
  </section>
</template>

<script lang="ts" setup>
import stepOneImage from './assets/onboarding-step-1.svg'
import stepTwoImage from './assets/onboarding-step-2.svg'
import stepThreeImage from './assets/onboarding-step-3.svg'
import { HFG_POSSIBLE_BONUS_ADD_ONS } from './highFrequencyGameConstants'

type Props = {
  config: {
    addOns: { name: string; maximumMultiplier?: number | null }[]
    numbers: { __typename?: string; selectableMax: number }[]
  }
}

const { config } = defineProps<Props>()

const { t } = useI18n()

const addOn = computed(
  () => config.addOns.find((addOn) => HFG_POSSIBLE_BONUS_ADD_ONS.includes(addOn.name)) ?? null,
)

const numberOfSpots = computed(
  () =>
    config.numbers.find(({ __typename }) => __typename === 'HighFrequency_NumbersConfig')
      ?.selectableMax,
)

const steps = computed(() => [
  {
    description: t('highfrequencygame.tutorial.step1.description', { value: numberOfSpots.value }),
    headline: t('highfrequencygame.tutorial.step1.headline'),
    image: stepOneImage,
  },
  {
    description: t('highfrequencygame.tutorial.step2.description', {
      addOn: t(`highfrequencygame.addon.${normalizeKey(addOn.value?.name ?? '')}`),
      value: addOn.value?.maximumMultiplier,
    }),
    headline: t('highfrequencygame.tutorial.step2.headline'),
    image: stepTwoImage,
  },
  {
    description: t('highfrequencygame.tutorial.step3.description'),
    headline: t('highfrequencygame.tutorial.step3.headline'),
    image: stepThreeImage,
  },
])

const mediaIsDesktop = useMediaQuery('screen and (width >= 768px)')
const isMounted = ref(false)
onMounted(() => {
  isMounted.value = true
})
const isDesktop = computed(() => isMounted.value && mediaIsDesktop.value)
const id = useId()
</script>

<style lang="scss" scoped>
.onboarding-carousel {
  overflow: hidden;
  margin: #{token('size/space/gap/050')} -#{token('size/space/gap/100')}
    #{token('size/space/gap/none')};
  padding: #{token('size/space/gap/none')} #{token('size/space/gap/none')}
    #{token('size/space/gap/200')} #{token('size/space/gap/100')};

  header {
    min-height: 2rem;
    margin-bottom: token('size/space/gap/100');
  }

  .card {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    overflow: clip;
    width: 100%;
    min-width: 15.3rem;
    max-width: 100%;
  }

  .image {
    display: block;
    width: 100%;
  }

  .carousel {
    position: relative;

    /* stylelint-disable-next-line selector-class-pattern */
    :deep(.lc-carousel__container) {
      overflow: visible;

      @include media-desktop {
        cursor: default;
      }
    }

    /* stylelint-disable-next-line selector-class-pattern */
    :deep(.lc-card__header) {
      padding: token('size/space/gap/none');
    }

    /* stylelint-disable-next-line selector-class-pattern */
    :deep(.lc-card__body-wrap) {
      flex-grow: 0;
    }
  }
}
</style>
